import "@/App.css";
import { HashRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AUTH_PHASE } from "@/lib/authSessionCoordinator";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Register from "@/pages/Register";
import Login from "@/pages/Login";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Dashboard from "@/pages/Dashboard";
import Lesson from "@/pages/Lesson";
import AdminLesson from "@/pages/AdminLesson";
import InstructorDashboard from "@/pages/InstructorDashboard";
import TheoryHome from "@/pages/TheoryHome";
import TheorySession from "@/pages/TheorySession";
import GatewayLaunch from "@/pages/GatewayLaunch";
import LearningPreferences from "@/pages/LearningPreferences";
import PlatformAdminCommercial from "@/pages/PlatformAdminCommercial";
import JoinCampaign from "@/pages/JoinCampaign";
import SchoolUniversityPathway from "@/pages/SchoolUniversityPathway";
import PathwayLanding from "@/pages/PathwayLanding";
import AdminInstructorOnboarding from "@/pages/AdminInstructorOnboarding";
import AdminLearnerOversight from "@/pages/AdminLearnerOversight";
import AdminTrainingRequests from "@/pages/AdminTrainingRequests";
import AcceptInstructorInvite from "@/pages/AcceptInstructorInvite";
import AcceptLearnerInvite from "@/pages/AcceptLearnerInvite";
import { storeLoginResume } from "@/lib/loginResume";
import { certRouteElements } from "./CertRoutes";

function authGateLoading(authReady, authPhase, user) {
  return authPhase === AUTH_PHASE.LOGGING_IN || !authReady || user === null;
}

function RequireAuth({ children }) {
  const { user, authReady, authPhase } = useAuth();
  if (authGateLoading(authReady, authPhase, user)) {
    return <p className="p-8" data-testid="auth-loading">Loading…</p>;
  }
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user, authReady, authPhase } = useAuth();
  if (authGateLoading(authReady, authPhase, user)) {
    return <p className="p-8" data-testid="auth-loading">Loading…</p>;
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function RequireInstructor({ children }) {
  const { user, authReady, authPhase } = useAuth();
  if (authGateLoading(authReady, authPhase, user)) {
    return <p className="p-8" data-testid="auth-loading">Loading…</p>;
  }
  if (user === false) {
    const hashPath = window.location.hash.replace(/^#/, "") || "/instructor";
    storeLoginResume(hashPath.startsWith("/") ? hashPath : `/${hashPath}`);
    return <Navigate to="/login" replace />;
  }
  if (user.role !== "instructor" && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function LegacyLessonRedirect() {
  const { lessonId } = useParams();
  return <Navigate to={lessonId ? `/lessons/${lessonId}` : "/dashboard"} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      {/* HashRouter: Render static sites require Dashboard rewrite for BrowserRouter deep links;
          _redirects is not applied by Render. Hash routes keep refresh/deep-link working on `/`. */}
      <HashRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/register" element={<Register />} />
            <Route path="/signup" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-instructor-invite" element={<AcceptInstructorInvite />} />
            <Route path="/accept-learner-invite" element={<AcceptLearnerInvite />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
            <Route path="/learning-preferences" element={<RequireAuth><LearningPreferences /></RequireAuth>} />
            <Route path="/lessons/:lessonId" element={<RequireAuth><Lesson /></RequireAuth>} />
            <Route path="/lessons" element={<RequireAuth><Lesson /></RequireAuth>} />
            <Route path="/lesson/:lessonId" element={<LegacyLessonRedirect />} />
            <Route path="/lesson" element={<Navigate to="/dashboard" replace />} />
            <Route path="/gateway/launch" element={<GatewayLaunch />} />
            <Route path="/instructor" element={<RequireInstructor><InstructorDashboard /></RequireInstructor>} />
            <Route path="/theory" element={<RequireAuth><TheoryHome /></RequireAuth>} />
            <Route path="/theory/session/:sessionId" element={<RequireAuth><TheorySession /></RequireAuth>} />
            <Route path="/join/:publicCode" element={<JoinCampaign />} />
            {certRouteElements}
            <Route path="/pathways/school-university" element={<SchoolUniversityPathway />} />
            <Route
              path="/pathways/general"
              element={
                <PathwayLanding
                  testId="pathway-general"
                  title="Learner driver — free theory"
                  lead="Start with Road Code theory, practice quizzes and mock tests. Request instructor-supported training when you are ready."
                  returnPath="/pathways/general"
                  interest="general_learner"
                />
              }
            />
            <Route
              path="/pathways/manual"
              element={
                <PathwayLanding
                  testId="pathway-manual"
                  title="Manual driving pathway"
                  lead="Free theory is available immediately after verification. Advanced manual training requires eligible booking and instructor activation."
                  returnPath="/pathways/manual"
                  interest="manual_driving"
                />
              }
            />
            <Route
              path="/pathways/overseas"
              element={
                <PathwayLanding
                  testId="pathway-overseas"
                  title="Overseas-driver support"
                  lead="Educational support for drivers new to New Zealand roads. Free theory first; advanced access follows eligibility and instructor activation."
                  returnPath="/pathways/overseas"
                  interest="overseas_driver"
                />
              }
            />
            <Route
              path="/pathways/older-driver"
              element={
                <PathwayLanding
                  testId="pathway-older-driver"
                  title="Older-driver / refresher"
                  lead="Confidence refresh using approved educational content. Free theory tier always available unless your account is suspended."
                  returnPath="/pathways/older-driver"
                  interest="older_driver_refresher"
                />
              }
            />
            <Route path="/admin/learners" element={<RequireAdmin><AdminLearnerOversight /></RequireAdmin>} />
            <Route path="/admin/training-requests" element={<RequireAdmin><AdminTrainingRequests /></RequireAdmin>} />
            <Route path="/admin" element={<RequireAdmin><AdminLesson /></RequireAdmin>} />
            <Route path="/admin/instructors" element={<RequireAdmin><AdminInstructorOnboarding /></RequireAdmin>} />
            <Route path="/admin/commercial" element={<RequireAdmin><PlatformAdminCommercial /></RequireAdmin>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </HashRouter>
    </AuthProvider>
  );
}
